import { useMemo } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useFacetedSearchCards } from '@sorare/core/src/hooks/search/useFacetedSearchCards';
import { joinFiltersWithAnd } from '@sorare/core/src/lib/algolia';

import useDefaultFilters from 'hooks/useDefaultFilters';

const facets = ['player.display_name'];
const params = { maxValuesPerFacet: 200 };

export const usePlayersOwnedByCurrentUser = () => {
  const { sport = Sport.FOOTBALL } = useSportContext();
  const { algoliaCardIndexes } = useConfigContext();
  const { currentUser } = useCurrentUserContext();
  const id = useMemo(() => currentUser?.id, [currentUser?.id]);
  const filters = useDefaultFilters({
    userId: idFromObject(id),
  });

  const facetFilters = useMemo(
    () => joinFiltersWithAnd([...filters, `sport:${sport}`]),
    [filters, sport]
  );

  const { results, loading, refresh } = useFacetedSearchCards({
    attributesToRetrieve: null,
    index: algoliaCardIndexes['Highest Average Score'],
    facetFilters,
    facets,
    params,
    hitsPerPage: 0,
    skip: !currentUser,
  });

  const playerDisplayNames = useMemo(
    () =>
      Object.entries(results?.facets?.['player.display_name'] || {}).map(
        ([playerDisplayName]) => {
          return playerDisplayName;
        }
      ),
    [results?.facets]
  );

  return {
    results,
    playerDisplayNames,
    loading,
    refresh,
  };
};
